import "./index.css";

import { lazy, Suspense, useEffect, type ReactNode } from "react";
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
  useRouteError,
  type HeadersArgs,
  type LinksFunction,
  type MetaFunction,
} from "react-router";

import { EffectsProvider } from "./components/effects/EffectsContext";
import { useDynamicFavicon } from "./components/favicon";
import { env } from "./env.server";
import { ConfigProvider, type AppConfig } from "./providers/ConfigProvider";
import { QueryProvider } from "./providers/QueryProvider";
import { readUnderConstructionConfig } from "./under-construction/config.server";
import { useUnderConstructionReload } from "./under-construction/hooks/useUnderConstructionReload";
import { setApiBase } from "./utils/client";

// The maintenance view carries three.js and the newsletter form; loading it
// on demand keeps them off every other document's hydration path.
const UnderConstructionView = lazy(() =>
  import("./under-construction/components/UnderConstructionView").then((m) => ({
    default: m.UnderConstructionView,
  })),
);

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Akshar:wght@300..700&family=Amiri&display=swap";

// A <link> fetches alongside the stylesheet; the @import it replaces waited
// behind it, in series, before first paint.
export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  { rel: "stylesheet", href: FONTS_URL },
];

export async function loader() {
  const config = {
    environment: env.ENVIRONMENT,
    convertkitFormId: env.CONVERTKIT_FORM_ID,
    apiOrigin: env.PUBLIC_API_ORIGIN,
  } satisfies AppConfig;

  // The boundary renders instead of Root and has no loader data, so the
  // thrown payload is how its shell receives the app config.
  if (await readUnderConstructionConfig()) {
    throw data(config, {
      status: 503,
      headers: { "Retry-After": String(env.UC_FLAG_RETRY_AFTER_SECONDS) },
    });
  }

  return config;
}

// Without this export the framework forwards only Set-Cookie; the 503's
// Retry-After would be dropped silently.
export function headers({ errorHeaders, loaderHeaders }: HeadersArgs) {
  return errorHeaders ?? loaderHeaders;
}

// The verdict is per document and live sessions reload on a flip (TUR-143);
// re-running this on client navigations would only add a round trip.
export const shouldRevalidate = () => false;

// The fallback for routes without their own meta (and for error documents).
export const meta: MetaFunction = () => [
  { title: "TurtleWave" },
  {
    name: "description",
    content: "TurtleWave - Understanding, Refinement, Humanity",
  },
];

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        {/* Title and description come through <Meta /> — root provides the
            fallback, routes override per entity. */}
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// The app's init, rendered once around every document this route produces.
// Root and ErrorBoundary are siblings the framework picks between, so each
// mounts the shell itself; config is absent on the error documents that
// carry none (404, unexpected failures).
function Shell({
  config,
  children,
}: {
  config?: AppConfig;
  children: ReactNode;
}) {
  // Before anything below can fetch: every query in the tree builds its URL
  // from this, and nothing runs a clientLoader at hydration.
  if (config) setApiBase(config.apiOrigin);
  useDynamicFavicon();
  useUnderConstructionReload(config?.environment);

  // Hydration beacon: effects run only after hydration, so this attribute
  // is browser-visible proof the app is live, and the mark is the
  // timestamp the smoke suite gates hydration latency on.
  useEffect(() => {
    document.documentElement.dataset.hydrated = "true";
    performance.mark("app:hydrated");
  }, []);

  // Analytics is a side effect nothing renders on; load it off the boot path.
  useEffect(() => {
    void import("./firebase-config").then((m) => m.initAnalytics());
  }, []);

  if (!config) return children;
  return (
    <ConfigProvider value={config}>
      <EffectsProvider>
        <QueryProvider>{children}</QueryProvider>
      </EffectsProvider>
    </ConfigProvider>
  );
}

// Last-resort boundary: the 503 arm is the designed maintenance page; the
// rest stays minimal until TUR-68/TUR-30 shape the real error surfaces.
// No useLoaderData here — it is silently undefined in a boundary.
export function ErrorBoundary() {
  const error = useRouteError();
  const maintenance =
    isRouteErrorResponse(error) && error.status === 503
      ? (error.data as AppConfig)
      : undefined;
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <Shell config={maintenance}>
      {maintenance ? (
        // Server-rendered in full; the bare document background shows under
        // the copy until the chunk hydrates it.
        <Suspense fallback={null}>
          <UnderConstructionView />
        </Suspense>
      ) : (
        <main className="flex min-h-dvh items-center justify-center p-6">
          <p className="text-center text-neutral-700">
            {notFound ? "Page not found." : "Something went wrong."}{" "}
            {/* A plain anchor on purpose: recovery from an error state should
                be a fresh document request, not a client-side navigation
                inside a possibly-broken session. */}
            <a href="/">Return to TurtleWave Home</a>
          </p>
        </main>
      )}
    </Shell>
  );
}

// Scroll restoration stays with AnimatedOutlet's location-state mechanism;
// adding <ScrollRestoration /> here would double-restore.
export default function Root() {
  const config = useLoaderData<typeof loader>();
  return (
    <Shell config={config}>
      <Outlet />
    </Shell>
  );
}
